import { useNavigation, useRoute } from '@react-navigation/native';
import { useMutation, useQuery } from '@apollo/client';
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import Toast from 'react-native-root-toast';

import {
	CORRECT_MARKET_RESTOCK,
	GET_MARKET_RESTOCK_CORRECTION,
} from '../../../common/graphql/serviceDocket';
import ServiceNoteButton from '../driver/components/ServiceNoteButton';
import createClientOperationId from '../../../common/utils/createClientOperationId';
import { useUserContext } from '../../../common/utils/context';
import { ROLE_PERMISSIONS } from '../../../common/constants';
import {
	getConfirmedRestockCorrectionPreview,
	getRestockBatchDraft,
	isRestockInventoryConfirmed,
	RESTOCK_CORRECTION_INVENTORY_CHECK_CHANGED_ERROR,
	RESTOCK_CORRECTION_REVIEW_REFRESHED_MESSAGE,
	validateMarketRestockCorrection,
} from '../../../common/utils/serviceDocket/marketRestockCorrection';

import { ErrorCallout, Text } from '../../components';
import { Card, Screen, SecondaryButton, StatusBadge } from '../../design/components';
import { Alert } from '../../components/other/Alert';
import TextInput from '../../components/input/TextInput';
import { useRefetchOnFocus } from '../../hooks';
import useBackLabel from '../../hooks/useBackLabel';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useOfflineQueue } from '../../offline';
import TrainingModeBanner from '../myDay/TrainingModeBanner';
import { getMyDayTrainingRouteParams, isMyDayTraining } from '../myDay/myDayTraining';
import { createCorrectMarketRestockStyles } from './CorrectMarketRestockView.styles';
import RestockCorrectionDock from './RestockCorrectionDock';
import RestockCorrectionHistory from './RestockCorrectionHistory';
import RestockCorrectionPreview from './RestockCorrectionPreview';
import RestockCorrectionProductCard from './RestockCorrectionProductCard';
import RestockCorrectionSaved from './RestockCorrectionSaved';

const CorrectMarketRestockView = () => {
	const navigation = useNavigation();
	const route = useRoute();
	const styles = useThemedStyles(createCorrectMarketRestockStyles);
	const { hasPermission, isSuperAdmin, tenant, user } = useUserContext();
	const { isOnline, queue } = useOfflineQueue();
	const trainingContext = route.params?.myDayTraining;
	const isTraining = isMyDayTraining(trainingContext);
	const scopedQueue = useMemo(() => (isTraining ? [] : queue), [isTraining, queue]);
	const timezone = tenant?.timezone;
	const serviceDocketId = route.params?.docketId;
	const [reason, setReason] = useState('');
	const [draftById, setDraftById] = useState({});
	const [formError, setFormError] = useState(null);
	const [pendingSubmission, setPendingSubmission] = useState(null);
	const [reloading, setReloading] = useState(false);
	const [reviewingConfirmedCorrection, setReviewingConfirmedCorrection] = useState(false);
	const [savedConfirmedCorrection, setSavedConfirmedCorrection] = useState(null);
	const initializedRevisionRef = useRef(null);
	const allowNavigationRef = useRef(false);
	const backLabel = useBackLabel();

	const {
		data,
		loading,
		error,
		refetch,
	} = useQuery(GET_MARKET_RESTOCK_CORRECTION, {
		variables: { id: serviceDocketId },
		fetchPolicy: isTraining ? 'no-cache' : 'network-only',
		skip: !serviceDocketId,
	});
	const [correctRestock, { loading: saving }] = useMutation(CORRECT_MARKET_RESTOCK);
	const serviceDocket = data?.getServiceDocket;
	const items = useMemo(() => (
		[...(serviceDocket?.pickList?.pickListItems || [])]
			.filter(item => item?.product)
			.sort((left, right) => (
				String(left.product?.name || '').localeCompare(String(right.product?.name || '')) ||
				String(left.selection || '').localeCompare(String(right.selection || ''))
			))
	), [serviceDocket?.pickList?.pickListItems]);
	const itemById = useMemo(() => new Map(items.map(item => [item.id, item])), [items]);

	useEffect(() => {
		if (!serviceDocket) {
			return;
		}
		const revisionKey = `${serviceDocket.id}:${Number(serviceDocket.restockRevision || 0)}`;
		if (initializedRevisionRef.current === revisionKey) {
			return;
		}

		setDraftById(items.reduce((drafts, item) => {
			const batch = getRestockBatchDraft(item.planogramItemExpiryDatesAtPick, timezone);
			drafts[item.id] = {
				quantity: Number(item.restockDeliveredQuantity || 0),
				expiryDate: batch.dateKey,
				initialExpiryDate: batch.dateKey,
				expiryDateChanged: false,
				batchLabel: batch.label,
				isMixedBatch: batch.isMixedBatch,
			};
			return drafts;
		}, {}));
		setReason('');
		setFormError(null);
		setPendingSubmission(null);
		setReviewingConfirmedCorrection(false);
		setSavedConfirmedCorrection(null);
		initializedRevisionRef.current = revisionKey;
	}, [items, serviceDocket, timezone]);

	const updateQuantity = useCallback((itemId, quantity) => {
		setDraftById(previous => ({
			...previous,
			[itemId]: {
				...previous[itemId],
				quantity,
			},
		}));
		setFormError(null);
		setReviewingConfirmedCorrection(false);
	}, []);

	const updateExpiryDate = useCallback((itemId, expiryDate) => {
		setDraftById(previous => ({
			...previous,
			[itemId]: {
				...previous[itemId],
				expiryDate,
				expiryDateChanged: expiryDate !== previous[itemId].initialExpiryDate,
			},
		}));
		setFormError(null);
		setReviewingConfirmedCorrection(false);
	}, []);

	const validation = useMemo(() => validateMarketRestockCorrection({
		items,
		draftById,
		timezone,
	}), [draftById, items, timezone]);
	const inventoryConfirmed = isRestockInventoryConfirmed(serviceDocket?.inventoryCheck);
	const confirmedPreview = useMemo(() => getConfirmedRestockCorrectionPreview({
		items,
		changes: validation.changes,
	}), [items, validation.changes]);
	const availability = serviceDocket?.marketRestockCorrectionAvailability;
	const isAssignedDriver = Boolean(user?.id && serviceDocket?.serviceRun?.driver?.id === user.id);
	const canManageServiceRuns = isSuperAdmin || hasPermission(ROLE_PERMISSIONS.ADD_EDIT_SERVICE_RUNS);
	const canCorrectMarketRestock = isAssignedDriver || canManageServiceRuns;
	const pendingMachineInventoryOperation = useMemo(() => (scopedQueue || []).some(entry => (
		['manualRestock', 'confirmStockLevels', 'confirmMachineValues'].includes(entry.configKey) &&
		(
			entry.meta?.machineId === serviceDocket?.machine?.id ||
			entry.meta?.serviceDocketId === serviceDocketId
		)
	)), [scopedQueue, serviceDocket?.machine?.id, serviceDocketId]);
	const hasUnsavedChanges = !savedConfirmedCorrection &&
		Boolean(reason.trim() || Object.keys(validation.changes).length);
	const staleRestockError = formError?.includes('Refresh and review the latest quantities.');
	const fieldsDisabled = saving || reloading || !isOnline || pendingMachineInventoryOperation ||
		!availability?.allowed || !canCorrectMarketRestock;
	const saveDisabled = fieldsDisabled || !reason.trim() ||
		!validation.hasQuantityChange || Boolean(validation.invalidMessage) ||
		Boolean(inventoryConfirmed && confirmedPreview.invalidMessage);

	useRefetchOnFocus(refetch, {
		enabled: !savedConfirmedCorrection && !hasUnsavedChanges && !saving && !reloading,
	});

	useEffect(() => {
		navigation.setOptions({
			gestureEnabled: !hasUnsavedChanges && !saving && !reloading,
			title: 'Correct delivered quantities',
		});
	}, [hasUnsavedChanges, navigation, reloading, saving]);

	useEffect(() => navigation.addListener('beforeRemove', (event) => {
		if (allowNavigationRef.current || (!hasUnsavedChanges && !saving && !reloading)) {
			return;
		}
		event.preventDefault();
		if (saving || reloading) {
			Toast.show('Wait for the delivered-quantity correction to finish');
			return;
		}
		Alert.alert(
			'Discard delivered-quantity correction?',
			'Your unsaved delivered-quantity correction will be lost.',
			[
				{ text: 'Keep editing', style: 'cancel' },
				{
					text: 'Discard',
					style: 'destructive',
					onPress: () => {
						allowNavigationRef.current = true;
						navigation.dispatch(event.data.action);
					},
				},
			],
			{ cancelable: false },
		);
	}), [hasUnsavedChanges, navigation, reloading, saving]);

	const reloadLatest = useCallback(() => {
		Alert.alert(
			'Reload latest restock?',
			'Your unsaved correction will be replaced so you can review the authoritative revision.',
			[
				{ text: 'Keep editing', style: 'cancel' },
				{
					text: 'Reload',
					onPress: async () => {
						setReloading(true);
						setFormError(null);
						setPendingSubmission(null);
						setReviewingConfirmedCorrection(false);
						try {
							await refetch();
						}
						catch (reloadError) {
							setFormError(reloadError?.message || 'Unable to reload the latest restock');
						}
						finally {
							setReloading(false);
						}
					},
				},
			],
			{ cancelable: false },
		);
	}, [refetch]);

	const reviewConfirmedCorrection = useCallback(() => {
		if (saveDisabled) {
			setFormError(confirmedPreview.invalidMessage || validation.invalidMessage || (
				!reason.trim()
					? 'Enter a reason for this correction.'
					: 'Change at least one delivered quantity before saving.'
			));
			return;
		}
		setFormError(null);
		setReviewingConfirmedCorrection(true);
	}, [confirmedPreview.invalidMessage, reason, saveDisabled, validation.invalidMessage]);

	const save = useCallback(async () => {
		if (saveDisabled || (inventoryConfirmed && !reviewingConfirmedCorrection)) {
			setFormError(confirmedPreview.invalidMessage || validation.invalidMessage || (
				inventoryConfirmed && !reviewingConfirmedCorrection
					? 'Review the resulting Missing and Extra amounts before confirming this correction.'
					: !reason.trim()
						? 'Enter a reason for this correction.'
						: 'Change at least one delivered quantity before saving.'
			));
			return;
		}

		const variablesWithoutOperation = {
			serviceDocketId,
			reason: reason.trim(),
			expectedRevision: Number(serviceDocket.restockRevision || 0),
			expectedInventoryCheck: serviceDocket.inventoryCheck ?? null,
			changes: validation.changes,
			...(isTraining ? { adminTraining: true } : {}),
		};
		const signature = JSON.stringify(variablesWithoutOperation);
		const operationId = pendingSubmission?.signature === signature
			? pendingSubmission.operationId
			: createClientOperationId('market-restock-correction');
		setPendingSubmission({ signature, operationId });
		setFormError(null);

		try {
			const result = await correctRestock({
				variables: {
					...variablesWithoutOperation,
					operationId,
				},
				fetchPolicy: 'no-cache',
			});
			Toast.show('Delivered quantities corrected');
			if (inventoryConfirmed) {
				const correctedDocket = result?.data?.correctMarketRestock;
				setSavedConfirmedCorrection({
					revision: Number(correctedDocket?.restockRevision || 0),
					availability: correctedDocket?.marketRestockStockReviewAvailability || null,
				});
				setPendingSubmission(null);
				return;
			}
			allowNavigationRef.current = true;
			navigation.goBack();
		}
		catch (saveError) {
			const message = saveError?.message || 'Unable to save the delivered-quantity correction';
			if (message.includes(RESTOCK_CORRECTION_INVENTORY_CHECK_CHANGED_ERROR)) {
				setReloading(true);
				try {
					const latestResult = await refetch();
					if (isRestockInventoryConfirmed(latestResult?.data?.getServiceDocket?.inventoryCheck)) {
						setFormError(RESTOCK_CORRECTION_REVIEW_REFRESHED_MESSAGE);
						setReviewingConfirmedCorrection(true);
						Toast.show(RESTOCK_CORRECTION_REVIEW_REFRESHED_MESSAGE);
						return;
					}
				}
				catch (refreshError) {
					console.log(refreshError);
				}
				finally {
					setReloading(false);
				}
			}
			setFormError(message);
			Toast.show(message);
		}
	}, [confirmedPreview.invalidMessage, correctRestock, inventoryConfirmed, isTraining, navigation, pendingSubmission, reason, refetch, reviewingConfirmedCorrection, saveDisabled, serviceDocket, serviceDocketId, validation]);

	const keepDeliveryCorrectionOnly = useCallback(() => {
		allowNavigationRef.current = true;
		navigation.goBack();
	}, [navigation]);

	const reviewStockLevels = useCallback(() => {
		if (!savedConfirmedCorrection?.availability?.allowed) {
			return;
		}
		allowNavigationRef.current = true;
		navigation.replace('ConfirmStockLevelsView', {
			docketId: serviceDocketId,
			restockCorrectionStockReviewRevision: savedConfirmedCorrection.revision,
			...getMyDayTrainingRouteParams(trainingContext),
		});
	}, [navigation, savedConfirmedCorrection, serviceDocketId, trainingContext]);

	const banner = <TrainingModeBanner trainingContext={trainingContext} isOnline={isOnline} />;

	if (loading && !serviceDocket) {
		return <Screen headerRight={<ServiceNoteButton />} backLabel={backLabel} banner={banner} heading="Correct delivered quantities" isLoading={true} />;
	}

	if (error || !serviceDocket) {
		// Server and network messages stay in the console; the screen explains in plain words.
		if (error) {
			console.warn('Restock correction could not be loaded:', error.message);
		}
		return (
			<Screen
				headerRight={<ServiceNoteButton />}
				backLabel={backLabel}
				banner={banner}
				heading="Correct delivered quantities"
				error={isOnline
					? 'Restock details could not be loaded. Try again, or go back and open the restock again.'
					: 'Restock details could not be loaded. Reconnect and try again.'}
				onRetry={() => refetch()}
			/>
		);
	}

	// Same machine name as the other stop pages' subheadings (passed by the stop screen).
	const marketName = route.params?.machineName ||
		serviceDocket.machine?.place?.name ||
		serviceDocket.machine?.assetId ||
		'Market';

	const dock = (
		<RestockCorrectionDock
			savedConfirmedCorrection={savedConfirmedCorrection}
			inventoryConfirmed={inventoryConfirmed}
			reviewingConfirmedCorrection={reviewingConfirmedCorrection}
			saving={saving}
			reloading={reloading}
			saveDisabled={saveDisabled}
			onSave={save}
			onReview={reviewConfirmedCorrection}
			onBackToEdit={() => setReviewingConfirmedCorrection(false)}
			onCancel={() => navigation.goBack()}
			onReviewStockLevels={reviewStockLevels}
			onKeepDeliveryCorrectionOnly={keepDeliveryCorrectionOnly}
		/>
	);

	return (
		<Screen
			headerRight={<ServiceNoteButton />}
			scroll={false}
			backLabel={backLabel}
			banner={banner}
			dock={dock}
		>
			<KeyboardAwareScrollView
				style={styles.container}
				contentContainerStyle={styles.content}
				// Keep the current product in view when the keyboard closes.
				enableResetScrollToCoords={false}
				keyboardShouldPersistTaps="handled"
			>
				<View style={styles.headingBlock}>
					<Text style={styles.title} accessibilityRole="header">Correct delivered quantities</Text>
					<Text style={styles.marketName}>{marketName}</Text>
				</View>
				<Text style={styles.intro}>
					Correct only the delivered quantities recorded for this completed restock. This does
					not correct a physical stock count. The original delivery remains in the history, and
					Sentry applies only the difference to stock. A reason and at least one delivered-quantity
					change are required.
				</Text>
				<StatusBadge
					size="small"
					label={`Authoritative revision ${Number(serviceDocket.restockRevision || 0)}`}
				/>
				{!availability?.allowed && (
					<ErrorCallout text={availability?.reason || 'This restock cannot be corrected.'} />
				)}
				{!isOnline && <ErrorCallout text="Reconnect before correcting this restock." />}
				{pendingMachineInventoryOperation && (
					<ErrorCallout text="Sync the pending restock or stock confirmation before correcting this restock." />
				)}
				{!canCorrectMarketRestock && (
					<ErrorCallout text="Only the assigned driver or a service-run manager can correct this restock." />
				)}
				{!reviewingConfirmedCorrection && validation.invalidMessage && (
					<ErrorCallout text={validation.invalidMessage} />
				)}
				{!reviewingConfirmedCorrection && inventoryConfirmed && confirmedPreview.invalidMessage && (
					<ErrorCallout text={confirmedPreview.invalidMessage} />
				)}
				{formError && formError !== validation.invalidMessage &&
					formError !== confirmedPreview.invalidMessage && (
					<>
						<ErrorCallout text={formError} />
						{staleRestockError && (
							<SecondaryButton
								label="Reload latest delivery"
								onPress={reloadLatest}
								loading={reloading}
							/>
						)}
					</>
				)}

				{savedConfirmedCorrection ? (
					<RestockCorrectionSaved savedCorrection={savedConfirmedCorrection} />
				) : inventoryConfirmed && reviewingConfirmedCorrection ? (
					<RestockCorrectionPreview confirmedPreview={confirmedPreview} reason={reason} />
				) : (
					<>
						{inventoryConfirmed && (
							<Card tone="warning">
								<Text style={styles.notice}>
									Stock for this service was already confirmed. Before saving, review the resulting Missing
									and Extra amounts. The confirmed count and current live count will remain unchanged.
								</Text>
							</Card>
						)}
						<Text style={styles.sectionTitle}>Delivered products</Text>
						{items.map(item => (
							<RestockCorrectionProductCard
								key={item.id}
								item={item}
								draft={draftById[item.id]}
								fieldsDisabled={fieldsDisabled}
								updateQuantity={updateQuantity}
								updateExpiryDate={updateExpiryDate}
							/>
						))}

						<Text style={styles.sectionTitle}>Reason for delivered-quantity correction</Text>
						<TextInput
							value={reason}
							disabled={fieldsDisabled}
							onChange={(value) => {
								setReason(value);
								setFormError(null);
							}}
							placeholder="Explain what was delivered incorrectly and why it is being corrected"
							multiline={true}
							numberOfLines={4}
							maxLength={1000}
							style={styles.reasonInput}
						/>
						<Text style={styles.hint}>
							Enter a reason and change at least one delivered quantity. Batch expiry can be corrected in the same revision.
						</Text>

						<RestockCorrectionHistory
							restockCorrections={serviceDocket.restockCorrections}
							itemById={itemById}
							timezone={timezone}
						/>
					</>
				)}
			</KeyboardAwareScrollView>
		</Screen>
	);
};

export default CorrectMarketRestockView;
